import type { Address } from '~/type/Cart'

const MIN_YEAR = 1900
export const FAQ_URL = 'https://www.swift.com.br/socio-swift#accordionFaq'
export const CASHBACK_RULE_URL = " https://www.swift.com.br/socio-swift/regulamento"



export const validateEmail = (value: string) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return regex.test(value) ? null : 'Digite um email válido'
}

export function isValidCPF(cpf: string): boolean {
  cpf = cpf.replace(/\D/g, '') // Remove non-numeric characters
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false

  const calculateCheckDigit = (base: number) =>
    (([...cpf].slice(0, base).reduce((sum, digit, index) => sum + parseInt(digit) * (base + 1 - index), 0) *
      10) %
      11) %
    10

  const checkDigit1 = calculateCheckDigit(9)
  const checkDigit2 = calculateCheckDigit(10)

  return checkDigit1 === parseInt(cpf[9]) && checkDigit2 === parseInt(cpf[10])
}

export function isValidCNPJ(cnpj: string): boolean {
  cnpj = cnpj.replace(/\D/g, '')

  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
    return false
  }

  const calculateCheckDigit = (cnpj: string, base: number) => {
    const weights =
      base === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] // Weights for the first check digit
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] // Weights for the second check digit

    const sum = [...cnpj].slice(0, base).reduce((acc, digit, index) => {
      return acc + parseInt(digit) * weights[index]
    }, 0)

    const remainder = sum % 11
    return remainder < 2 ? 0 : 11 - remainder
  }

  const checkDigit1 = calculateCheckDigit(cnpj, 12)
  const checkDigit2 = calculateCheckDigit(cnpj, 13)

  return checkDigit1 === parseInt(cnpj[12]) && checkDigit2 === parseInt(cnpj[13])
}

export const validateName = (value: string) => {
  const regex = /^[a-zA-Z\s\u00C0-\u00FF]+$/

  if (!value || !value.replace(/\s/g, '')) {
    return 'Este campo é obrigatório'
  }

  return regex.test(value) ? null : 'Digite apenas letras (sem números ou símbolos especiais)'
}

export const validateInscricaoEstadual = (value: string) => {
  const regex = /^\d+$/
  return regex.test(value) ? null : 'Digite uma inscrição estadual válida'
}

export const validateCPF = (value: string) => {
  return isValidCPF(value) ? null : 'Número de CPF inválido'
}
export const validateCNPJ = (value: string) => {
  return isValidCNPJ(value) ? null : 'Número de CNPJ inválido'
}

export const validatePhone = (value: string) => {
  const regex = /^\(\d{2}\)\d{5}-\d{4}$/
  return regex.test(value) ? null : 'Digite um telefone válido no formato (99)99999-9999'
}

export const validatePassword = (value: string) => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/
  return regex.test(value) ? null : 'A senha não corresponde aos requisitos mínimos de diretiva de senha.'
}

export const validateDate = (value: string) => {
  const inputDate = new Date(value)
  const today = new Date()

  if (!value) {
    return 'Insira um valor válido, o campo está incompleto ou tem uma data inválida.'
  }

  if (inputDate.getFullYear() < MIN_YEAR || inputDate > today) {
    return 'Data inválida.'
  }
}

export const maskCPF = (value: string | number) =>
  String(value)
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')

export const maskCNPJ = (value: string) => {
  const formattedValue = value
    .replace(/[^\d]/g, '')
    .replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})$/, '$1.$2.$3/$4-$5') //Add points, slash and dash

  return formattedValue
}

export const maskInscricaoEstadual = (value: string) => {
  const formattedValue = value.replace(/[^\d]/g, '')
  return formattedValue
}

export const maskCreditCard = (value: string) => {
  const formattedValue = value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')

  return formattedValue
}

export const maskBirthday = (value: string) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/

  if (!regex.test(value)) {
    return 'Digite uma data válida no formato AAAA-MM-DD'
  }

  const [year, month, day] = value.split('-')

  const date = new Date(Number(year), Number(month) - 1, Number(day))
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() + 1 !== Number(month) ||
    date.getDate() !== Number(day)
  ) {
    return 'Digite uma data válida'
  }

  return `${day}/${month}/${year}`
}

export const removeSpecialChars = (value: string) => {
  return value.replace(/[^a-zA-Z\s\u00C0-\u00FF]/g, '')
}

export const maskPhone = (value: string | number) =>
  String(value)
    .replace(/[^\d-]/g, '')
    .replace(/^(\d{2})(\d)/g, '($1)$2')
    .replace(/(\d{5})(\d{4})$/, '$1-$2')

export const removeDdiFromPhone = (phone?: string): string => {
  return phone ? phone.replace(/^\+55/, '') : ''
}

export const validateAddressData = (data: Address | null) => {
  if (!data) return false
  const requiredFields = ['street', 'city', 'state']
  const hasEmptyRequiredFields = requiredFields.some(
    (field) => !data[field as keyof Address] || String(data[field as keyof Address]).trim() === '',
  )
  const state = String(data.state).trim()
  const isValidState = state.length === 2 || state.length === 0
  return !(hasEmptyRequiredFields || !isValidState)
}
